import { UserValue } from '@core-features/core';

export interface AuthPermissions {
  roles?: string[];
  partnerTypes?: number[];
  userGroups?: string[];
}

const isEmpty = (values?: unknown[]): boolean => !values || values.length === 0;

/**
 * Synchronous, side-effect-free permission checks against a user value.
 *
 * - For reactive UI, pass `userService.$user()` inside a `computed()`; in
 *   streams, map over `userService.user$`.
 * - Every check returns `false` for a non-empty requirement until the user is
 *   loaded (`null` / `undefined` user).
 * - `has()` treats an empty dimension (`undefined` or `[]`) as passing, while
 *   the single-dimension checks return `false` for `undefined` or `[]`.
 * - Hiding UI is not access control: routes must keep `canActivatePermission`.
 *
 * @example
 * readonly visibleNavItems = computed(() => {
 *   const user = this.userService.$user();
 *   return this.navItems.filter((item) =>
 *     UserPermissions.has(user, item.permissions),
 *   );
 * });
 */
export const UserPermissions = {
  /**
   * Checks whether the user meets the given permissions.
   *
   * - `permissions` `null` / `undefined` -> `true`.
   * - A dimension that is `undefined` or `[]` passes.
   * - All non-empty dimensions must match (AND); within a dimension one match
   *   is enough (OR).
   * - A `null` / `undefined` user fails any non-empty requirement and passes
   *   when nothing is required, so this returns `false` for a non-empty
   *   requirement until the user is loaded.
   *
   * Same semantics as `canActivatePermission`. Use it to hide UI only — hiding
   * is not access control, routes must keep the guard. See
   * {@link UserPermissions} for usage with `computed()`.
   */
  has: (user: UserValue, permissions?: AuthPermissions | null): boolean => {
    if (!permissions) {
      return true;
    }
    const { roles, partnerTypes, userGroups } = permissions;
    return (
      (isEmpty(roles) || UserPermissions.hasOneRole(user, roles)) &&
      (isEmpty(partnerTypes) ||
        UserPermissions.isOneOfPartnerTypes(user, partnerTypes)) &&
      (isEmpty(userGroups) ||
        UserPermissions.isInOneOfUserGroups(user, userGroups))
    );
  },

  /**
   * Checks whether the user has at least one of the roles.
   * Returns `false` for `undefined` / `[]` roles and for a `null` / `undefined` user.
   */
  hasOneRole: (user: UserValue, roles?: string[]): boolean =>
    !!user?.roles &&
    user.roles.length > 0 &&
    !!roles &&
    roles.some((role) => user.roles.includes(role)),

  /**
   * Checks whether the user is one of the partner types.
   * Returns `false` for `undefined` / `[]` partner types and for a `null` / `undefined` user.
   */
  isOneOfPartnerTypes: (user: UserValue, partnerTypes?: number[]): boolean =>
    !!user?.partnertype &&
    !!partnerTypes &&
    partnerTypes.includes(user.partnertype),

  /**
   * Checks whether the user is in at least one of the user groups.
   * Returns `false` for `undefined` / `[]` user groups and for a `null` / `undefined` user.
   */
  isInOneOfUserGroups: (user: UserValue, userGroups?: string[]): boolean =>
    !!user?.user_group &&
    user.user_group.length > 0 &&
    !!userGroups &&
    user.user_group.some((group) => userGroups.includes(group)),
} as const;
